import type { BreakRange } from './types.js';

export interface MinuteRange {
  min: number;
  max: number;
}

export interface ShiftMinutes {
  worked: number;
  night: MinuteRange;
}

function parseClock(value: string): number {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) throw new Error(`INVALID_TIME:${value}`);
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) throw new Error(`INVALID_TIME:${value}`);
  return hour * 60 + minute;
}

function normalizedEnd(start: number, end: number): number {
  return end <= start ? end + 1440 : end;
}

function overlap(startA: number, endA: number, startB: number, endB: number): number {
  return Math.max(0, Math.min(endA, endB) - Math.max(startA, startB));
}

function normalizeBreak(range: BreakRange, shiftStart: number, shiftEnd: number): [number, number] {
  let start = parseClock(range.start);
  let end = normalizedEnd(start, parseClock(range.end));
  if (start < shiftStart) {
    start += 1440;
    end += 1440;
  }
  if (start < shiftStart || end > shiftEnd || end <= start) throw new Error('INVALID_BREAK_RANGE');
  return [start, end];
}

export function calculateShiftMinutes(
  startValue: string,
  endValue: string,
  breakRanges: BreakRange[] | undefined,
  unknownBreakMinutes: number | undefined,
): ShiftMinutes {
  const start = parseClock(startValue);
  const end = normalizedEnd(start, parseClock(endValue));
  const duration = end - start;
  const nightWindows: [number, number][] = [
    [-120, 360], [1320, 1800],
  ];
  const rawNight = nightWindows.reduce((sum, [windowStart, windowEnd]) => sum + overlap(start, end, windowStart, windowEnd), 0);

  if (breakRanges && breakRanges.length > 0) {
    const normalized = breakRanges.map((range) => normalizeBreak(range, start, end)).sort((a, b) => a[0] - b[0]);
    let breakTotal = 0;
    let nightBreak = 0;
    for (let index = 0; index < normalized.length; index += 1) {
      const current = normalized[index];
      if (!current) continue;
      const previous = normalized[index - 1];
      if (previous && current[0] < previous[1]) throw new Error('INVALID_BREAK_RANGE');
      const [breakStart, breakEnd] = current;
      breakTotal += breakEnd - breakStart;
      nightBreak += nightWindows.reduce((sum, [windowStart, windowEnd]) => sum + overlap(breakStart, breakEnd, windowStart, windowEnd), 0);
    }
    if (breakTotal > duration) throw new Error('INVALID_BREAK_RANGE');
    const night = rawNight - nightBreak;
    return { worked: duration - breakTotal, night: { min: night, max: night } };
  }

  const breakTotal = unknownBreakMinutes ?? 0;
  if (!Number.isInteger(breakTotal) || breakTotal < 0 || breakTotal > duration) throw new Error('INVALID_BREAK_RANGE');
  const nonNight = duration - rawNight;
  return {
    worked: duration - breakTotal,
    night: {
      min: Math.max(0, rawNight - breakTotal),
      max: rawNight - Math.max(0, breakTotal - nonNight),
    },
  };
}
